import React, { useEffect, useMemo, useState } from 'react';
import { arrayUnion, collection, deleteField, doc, getDoc, getDocs, runTransaction, serverTimestamp, updateDoc } from 'firebase/firestore';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import Spinner from './Spinner.js';
import MultiFileUpload from './components/MultiFileUpload.js';
import { getDownloadURL, ref, storage, uploadBytes } from './firebaseUtils.js';
import { buildPaymentRequestWorkflow } from './paymentRequestWorkflow.js';

const getToday = () => {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${today.getFullYear()}-${month}-${day}`;
};

const createLineItem = () => ({ againstHead: '', documentNo: '', description: '', amount: '' });

const getRequestYear = (requestDate) => {
  const parsedYear = Number.parseInt(String(requestDate || '').slice(0, 4), 10);
  return Number.isFinite(parsedYear) ? parsedYear : new Date().getFullYear();
};

const CreatePaymentRequest = ({ db, user }) => {
  const navigate = useNavigate();
  const { requestId } = useParams();
  const isEditMode = Boolean(requestId);
  const [formData, setFormData] = useState({
    requestType: '',
    priority: 'Standard',
    urgentRemarks: '',
    legalEntityId: '',
    legalEntityName: '',
    costCenterId: '',
    costCenterName: '',
    departmentId: '',
    departmentName: '',
    requestDate: getToday(),
    payeeName: '',
    payeeId: '',
    payeeCode: '',
    payeeType: '',
    orderPoRo: '',
    payUntil: '',
    paymentAdvanceRemarks: '',
    outstandingPreviousAdvance: '',
    activitySupplyEndDate: '',
    settlementDueDate: '',
    invoiceNumber: '',
    invoiceDate: '',
    invoiceGrossAmount: '',
    unpaidInvoiceBalance: '',
    invoiceDueDate: '',
    grnServiceAcceptanceReference: '',
    payrollPeriodStartDate: '',
    payrollPeriodEndDate: '',
    approvedPayrollBatchReference: '',
    expenseDate: '',
    expenseSupportingReference: '',
    pettyExpenseReplenishmentReference: '',
    statutoryPaymentCategory: '',
    statutoryPeriodStartDate: '',
    statutoryPeriodEndDate: '',
    challanAssessmentReference: '',
    businessExpenseDeclaration: false
  });
  const [lineItems, setLineItems] = useState([createLineItem()]);
  const [firms, setFirms] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [payeeOptions, setPayeeOptions] = useState([]);
  const [isLoadingPayees, setIsLoadingPayees] = useState(false);
  const [showPayeeOptions, setShowPayeeOptions] = useState(false);
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingRequest, setIsLoadingRequest] = useState(isEditMode);

  useEffect(() => {
    if (!isEditMode) {
      setIsLoadingRequest(false);
      return undefined;
    }

    const loadRequest = async () => {
      try {
        const snapshot = await getDoc(doc(db, 'payment_requests', requestId));
        if (!snapshot.exists()) {
          toast.error('Payment request not found.');
          navigate('/payments-requests');
          return;
        }

        const savedRequest = snapshot.data();
        setFormData((current) => ({
          ...current,
          requestType: savedRequest.requestType || '',
          priority: savedRequest.priority || 'Standard',
          urgentRemarks: savedRequest.urgentRemarks || '',
          legalEntityId: savedRequest.legalEntityId || '',
          legalEntityName: savedRequest.legalEntityName || savedRequest.legalEntity || savedRequest.costCentre || '',
          costCenterId: savedRequest.costCenterId || '',
          costCenterName: savedRequest.costCenterName || '',
          departmentId: savedRequest.departmentId || '',
          departmentName: savedRequest.departmentName || '',
          requestDate: savedRequest.requestDate || getToday(),
          payeeName: savedRequest.payeeName || '',
          payeeId: savedRequest.payeeId || '',
          payeeCode: savedRequest.payeeCode || '',
          payeeType: savedRequest.payeeType || '',
          orderPoRo: savedRequest.orderPoRo || '',
          payUntil: savedRequest.payUntil || '',
          paymentAdvanceRemarks: savedRequest.paymentAdvanceRemarks || '',
          outstandingPreviousAdvance: savedRequest.outstandingPreviousAdvance || '',
          activitySupplyEndDate: savedRequest.activitySupplyEndDate || '',
          settlementDueDate: savedRequest.settlementDueDate || '',
          invoiceNumber: savedRequest.invoiceNumber || '',
          invoiceDate: savedRequest.invoiceDate || '',
          invoiceGrossAmount: savedRequest.invoiceGrossAmount || '',
          unpaidInvoiceBalance: savedRequest.unpaidInvoiceBalance || '',
          invoiceDueDate: savedRequest.invoiceDueDate || '',
          grnServiceAcceptanceReference: savedRequest.grnServiceAcceptanceReference || '',
          payrollPeriodStartDate: savedRequest.payrollPeriodStartDate || '',
          payrollPeriodEndDate: savedRequest.payrollPeriodEndDate || '',
          approvedPayrollBatchReference: savedRequest.approvedPayrollBatchReference || '',
          expenseDate: savedRequest.expenseDate || '',
          expenseSupportingReference: savedRequest.expenseSupportingReference || '',
          pettyExpenseReplenishmentReference: savedRequest.pettyExpenseReplenishmentReference || '',
          statutoryPaymentCategory: savedRequest.statutoryPaymentCategory || '',
          statutoryPeriodStartDate: savedRequest.statutoryPeriodStartDate || '',
          statutoryPeriodEndDate: savedRequest.statutoryPeriodEndDate || '',
          challanAssessmentReference: savedRequest.challanAssessmentReference || '',
          businessExpenseDeclaration: Boolean(savedRequest.businessExpenseDeclaration)
        }));
        setLineItems(savedRequest.lineItems?.length
          ? savedRequest.lineItems.map((item) => ({ ...createLineItem(), ...item }))
          : [createLineItem()]);
        setAttachmentFiles(Array.isArray(savedRequest.attachments) ? savedRequest.attachments : []);
      } catch (error) {
        console.error('Unable to load payment request for editing:', error);
        toast.error('Unable to load payment request.');
      } finally {
        setIsLoadingRequest(false);
      }
    };

    loadRequest();
    return undefined;
  }, [db, isEditMode, navigate, requestId]);

  useEffect(() => {
    const loadFirms = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'firms'));
        setFirms(snapshot.docs.map((firm) => ({ id: firm.id, name: firm.data().name })).filter((firm) => firm.name));
      } catch (error) {
        console.error('Unable to load cost centers:', error);
        toast.error('Unable to load cost centers.');
      }
    };

    loadFirms();
  }, [db]);

  useEffect(() => {
    const loadRequestMasterData = async () => {
      try {
        const [costCentersSnapshot, departmentsSnapshot] = await Promise.all([
          getDoc(doc(db, 'system_config', 'cost_centers')),
          getDoc(doc(db, 'system_config', 'departments'))
        ]);
        const getActiveItems = (snapshot) => (Array.isArray(snapshot.data()?.items) ? snapshot.data().items : [])
          .filter((item) => item?.id && item?.name && item.active !== false)
          .sort((first, second) => Number(first.sortOrder || 0) - Number(second.sortOrder || 0));

        setCostCenters(getActiveItems(costCentersSnapshot));
        setDepartments(getActiveItems(departmentsSnapshot));
      } catch (error) {
        console.error('Unable to load payment request master data:', error);
        toast.error('Unable to load cost centers and departments.');
      }
    };

    loadRequestMasterData();
  }, [db]);

  useEffect(() => {
    const sourceCollection = ['Supplier Invoice', 'Supplier Advance'].includes(formData.requestType)
      ? 'vendors'
      : null;

    if (!sourceCollection) {
      setPayeeOptions([]);
      setIsLoadingPayees(false);
      return undefined;
    }

    const loadPayees = async () => {
      setIsLoadingPayees(true);
      try {
        const snapshot = await getDocs(collection(db, sourceCollection));
        setPayeeOptions(snapshot.docs.map((payee) => ({
          id: payee.id,
          name: payee.data().name,
          code: payee.data().code,
          type: payee.data().type,
          status: payee.data().status
        })).filter((payee) => (
          payee.name
          && String(payee.status || '').toLowerCase() === 'active'
        )));
      } catch (error) {
        console.error(`Unable to load ${sourceCollection}:`, error);
        toast.error('Unable to load vendors.');
        setPayeeOptions([]);
      } finally {
        setIsLoadingPayees(false);
      }
    };

    loadPayees();
    return undefined;
  }, [db, formData.requestType]);

  const amountRequested = useMemo(() => lineItems.reduce(
    (total, item) => total + (Number(item.amount) || 0),
    0
  ), [lineItems]);
  const matchingPayeeOptions = payeeOptions.filter((payee) => (
    payee.name.toLowerCase().includes(formData.payeeName.toLowerCase())
  ));
  const employeePayeeRequestTypes = ['Employee Advance', 'Reimbursement'];
  const supplierPayeeRequestTypes = ['Supplier Invoice', 'Supplier Advance'];
  const isEmployeePayeeRequest = employeePayeeRequestTypes.includes(formData.requestType);
  const isSupplierPayeeRequest = supplierPayeeRequestTypes.includes(formData.requestType);
  const showAttachments = formData.requestType
    && !['Employee Advance', 'Salary', 'Statutory Payment'].includes(formData.requestType);
  const attachmentLabel = 'Supporting Documents';

  const updateForm = (event) => {
    const { name, value, checked } = event.target;
    if (name === 'requestType') setShowPayeeOptions(false);
    setFormData((current) => {
      if (name === 'requestType') {
        const isEmployeeRequest = employeePayeeRequestTypes.includes(value);
        const isSupplierRequest = supplierPayeeRequestTypes.includes(value);
        return {
          ...current,
          requestType: value,
          payeeType: isEmployeeRequest ? 'Employee' : '',
          payeeName: isEmployeeRequest ? (user?.displayName || user?.email || '') : '',
          payeeId: isEmployeeRequest ? (user?.uid || '') : '',
          payeeCode: ''
        };
      }

      if (name === 'payeeType') {
        return {
          ...current,
          payeeType: value,
          payeeName: value === 'Employee' ? (user?.displayName || user?.email || '') : '',
          payeeId: value === 'Employee' ? (user?.uid || '') : '',
          payeeCode: ''
        };
      }

      if (name === 'costCenterId') {
        const selectedCostCenter = costCenters.find((costCenter) => costCenter.id === value);
        return {
          ...current,
          costCenterId: value,
          costCenterName: selectedCostCenter?.name || ''
        };
      }

      if (name === 'legalEntityId') {
        const selectedFirm = firms.find((firm) => firm.id === value);
        return {
          ...current,
          legalEntityId: value,
          legalEntityName: selectedFirm?.name || ''
        };
      }

      if (name === 'departmentId') {
        const selectedDepartment = departments.find((department) => department.id === value);
        return {
          ...current,
          departmentId: value,
          departmentName: selectedDepartment?.name || ''
        };
      }

      if (name === 'businessExpenseDeclaration') {
        return { ...current, businessExpenseDeclaration: checked };
      }

      return {
        ...current,
        [name]: value,
        ...(name === 'payeeName' ? {
          payeeId: '',
          payeeCode: '',
          ...(isSupplierPayeeRequest ? { payeeType: '' } : {})
        } : {})
      };
    });
  };

  const selectPayee = (payee) => {
    setFormData((current) => ({
      ...current,
      payeeName: payee.name,
      payeeId: payee.id,
      payeeCode: payee.code || '',
      payeeType: payee.type || ''
    }));
    setShowPayeeOptions(false);
  };

  const updateLineItem = (index, field, value) => {
    setLineItems((current) => current.map((item, itemIndex) => (
      itemIndex === index ? { ...item, [field]: value } : item
    )));
  };

  const removeLineItem = (index) => {
    setLineItems((current) => current.filter((_, itemIndex) => itemIndex !== index));
  };

  const getManagerAssignment = async () => {
    const requesterEmail = String(user?.email || '').trim().toLowerCase();
    if (!requesterEmail) throw new Error('Unable to identify the requester.');

    const requesterSnapshot = await getDoc(doc(db, 'authorized_users', requesterEmail));
    const managerEmail = String(requesterSnapshot.data()?.reportingManagerEmail || '').trim().toLowerCase();
    if (!managerEmail) throw new Error('No reporting manager has been assigned to this user.');

    const managerSnapshot = await getDoc(doc(db, 'authorized_users', managerEmail));
    return {
      managerEmail,
      managerName: managerSnapshot.data()?.displayName || managerEmail
    };
  };

  const uploadAttachments = async (targetRequestId) => {
    if (!showAttachments) return [];

    return Promise.all(attachmentFiles.map(async (attachment, index) => {
      if (attachment.url) {
        return {
          name: attachment.name || `Attachment ${index + 1}`,
          size: attachment.size || 0,
          type: attachment.type || '',
          url: attachment.url,
          storagePath: attachment.storagePath || ''
        };
      }

      const file = attachment.file || attachment;
      const safeName = String(file.name || `attachment-${index + 1}`).replace(/[^a-zA-Z0-9._-]/g, '_');
      const storagePath = `payment_requests/${targetRequestId}/attachments/${Date.now()}_${index}_${safeName}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file, { contentType: file.type || 'application/octet-stream' });

      return {
        name: file.name || `Attachment ${index + 1}`,
        size: file.size || 0,
        type: file.type || '',
        url: await getDownloadURL(storageRef),
        storagePath
      };
    }));
  };

  const saveRequest = async (shouldSubmit) => {
    setIsSaving(true);
    try {
      const normalizedLineItems = lineItems.map((item) => ({
        againstHead: item.againstHead.trim(),
        documentNo: item.documentNo.trim(),
        description: item.description.trim(),
        amount: Number(item.amount) || 0
      }));
      const workflow = buildPaymentRequestWorkflow(user, amountRequested);
      const managerAssignment = shouldSubmit && !workflow.skipManagerApproval ? await getManagerAssignment() : null;
      const status = shouldSubmit ? workflow.initialStatus : 'Draft';
      const submissionAction = workflow.submissionAction;
      const savedRequestId = requestId || doc(collection(db, 'payment_requests')).id;
      const attachments = await uploadAttachments(savedRequestId);
      const requestPayload = {
        ...formData,
        lineItems: normalizedLineItems,
        attachments,
        requestedAmount: amountRequested,
        status,
        workflow: shouldSubmit ? workflow : null,
        updatedAt: serverTimestamp(),
        submittedAt: shouldSubmit ? serverTimestamp() : null,
        ...(managerAssignment || {})
      };

      if (isEditMode) {
        await updateDoc(doc(db, 'payment_requests', requestId), {
          ...requestPayload,
          legalEntity: deleteField(),
          costCentre: deleteField(),
          ...(workflow.skipManagerApproval && shouldSubmit ? { managerEmail: deleteField(), managerName: deleteField() } : {}),
          auditTrail: arrayUnion({
            action: shouldSubmit
              ? submissionAction
              : 'saved_draft',
            at: new Date().toISOString(),
            byUid: user.uid,
            byName: user.displayName || user.email || ''
          })
        });
      } else {
        const savedAt = new Date().toISOString();
        const requestYear = getRequestYear(formData.requestDate);
        const counterRef = doc(db, 'payment_request_counters', String(requestYear));
        const requestRef = doc(db, 'payment_requests', savedRequestId);

        await runTransaction(db, async (transaction) => {
          const counterSnapshot = await transaction.get(counterRef);
          const nextSequence = (counterSnapshot.data()?.lastSequence || 0) + 1;
          const requestNumber = `PRV/${requestYear}/${String(nextSequence).padStart(4, '0')}`;

          transaction.set(counterRef, {
            lastSequence: nextSequence,
            updatedAt: serverTimestamp()
          }, { merge: true });
          transaction.set(requestRef, {
            ...requestPayload,
            requestNumber,
            requesterUid: user.uid,
            requesterEmail: user.email || '',
            requesterName: user.displayName || user.email || '',
            auditTrail: [{
              action: shouldSubmit
                ? submissionAction
                : 'saved_draft',
              at: savedAt,
              byUid: user.uid,
              byName: user.displayName || user.email || ''
            }],
            createdAt: serverTimestamp()
          });
        });
      }

      toast.success(shouldSubmit ? 'Payment request submitted.' : 'Draft saved.');
      navigate(isEditMode ? `/payment-requests/${savedRequestId}` : '/payments-requests', { state: { createdRequestId: savedRequestId } });
    } catch (error) {
      console.error('Unable to save payment request:', error);
      toast.error(error?.message === 'No reporting manager has been assigned to this user.'
        ? error.message
        : 'Unable to save the payment request. Please try again.');
      setIsSaving(false);
    }
  };

  const inputStyle = {
    width: '100%',
    boxSizing: 'border-box',
    padding: '10px 12px',
    border: '1px solid #cbd5e1',
    borderRadius: '7px',
    fontSize: '14px',
    color: '#0f172a',
    background: '#fff'
  };
  const labelStyle = { display: 'block', marginBottom: '6px', color: '#334155', fontSize: '13px', fontWeight: '700' };
  const sectionStyle = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '22px', marginBottom: '20px' };

  if (isLoadingRequest) return <Spinner />;

  return (
    <div style={{ padding: '24px', maxWidth: '1150px', margin: '0 auto' }}>
      <button
        type="button"
        onClick={() => navigate('/payments-requests')}
        style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', border: 0, padding: 0, marginBottom: '18px', color: '#2563eb', background: 'transparent', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}
      >
        <ArrowLeft size={17} /> Back to Payment Requests
      </button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginBottom: '24px' }}>
        <div>
          <h2 style={{ margin: 0, color: '#0f172a' }}>{isEditMode ? 'Edit Payment Request' : 'New Payment Request'}</h2>
          <p style={{ margin: '6px 0 0', color: '#64748b' }}>Enter the payment details and amount breakup.</p>
        </div>
        <div style={{ display: 'flex', gap: '12px', flexShrink: 0 }}>
          <button type="button" disabled={isSaving} onClick={() => saveRequest(false)} style={{ padding: '11px 18px', border: '1px solid #64748b', borderRadius: '7px', background: '#fff', color: '#334155', cursor: isSaving ? 'not-allowed' : 'pointer', fontWeight: '700' }}>Save Draft</button>
          <button type="button" disabled={isSaving} onClick={() => saveRequest(true)} style={{ padding: '11px 18px', border: 0, borderRadius: '7px', background: '#2563eb', color: '#fff', cursor: isSaving ? 'not-allowed' : 'pointer', fontWeight: '700' }}>{isSaving ? 'Saving…' : 'Submit'}</button>
        </div>
      </div>

      <form onSubmit={(event) => event.preventDefault()}>
        <section style={sectionStyle}>
          <h3 style={{ margin: '0 0 18px', color: '#1e293b', fontSize: '16px' }}>Request Details</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '18px' }}>
            <label style={labelStyle}>Requester<input value={user?.displayName || user?.email || ''} readOnly style={{ ...inputStyle, background: '#f8fafc', cursor: 'not-allowed' }} /></label>
            <label style={labelStyle}>Request Type<select name="requestType" value={formData.requestType || ''} onChange={updateForm} style={inputStyle}><option value="">Select request type</option><option value="Employee Advance">Employee Advance</option><option value="Supplier Invoice">Supplier Invoice</option><option value="Supplier Advance">Supplier Advance</option><option value="Reimbursement">Reimbursement</option><option value="Petty Expenses">Petty Expenses</option><option value="Salary">Salary</option><option value="Statutory Payment">Statutory Payment</option></select></label>
            <label style={labelStyle}>Priority<select name="priority" value={formData.priority} onChange={updateForm} style={inputStyle}><option value="Standard">Standard</option><option value="Urgent">Urgent</option></select></label>
            <label style={labelStyle}>Legal Entity<select name="legalEntityId" value={formData.legalEntityId} onChange={updateForm} style={inputStyle}><option value="">Select legal entity</option>{firms.map((firm) => <option key={firm.id} value={firm.id}>{firm.name}</option>)}</select></label>
            <label style={labelStyle}>Cost Center<select name="costCenterId" value={formData.costCenterId} onChange={updateForm} style={inputStyle}><option value="">Select cost center</option>{costCenters.map((costCenter) => <option key={costCenter.id} value={costCenter.id}>{costCenter.name}</option>)}</select></label>
            <label style={labelStyle}>Department<select name="departmentId" value={formData.departmentId} onChange={updateForm} style={inputStyle}><option value="">Select department</option>{departments.map((department) => <option key={department.id} value={department.id}>{department.name}</option>)}</select></label>
            <label style={labelStyle}>Request Date<input type="date" name="requestDate" value={formData.requestDate} onChange={updateForm} style={inputStyle} /></label>
            <label style={labelStyle}>Pay Until<input type="date" name="payUntil" value={formData.payUntil} onChange={updateForm} style={inputStyle} /></label>
          </div>

          {formData.priority === 'Urgent' && (
            <label style={{ ...labelStyle, marginTop: '18px' }}>Urgent Remarks<textarea name="urgentRemarks" value={formData.urgentRemarks} onChange={updateForm} rows="3" placeholder="Explain why this request is urgent" style={{ ...inputStyle, resize: 'vertical' }} /></label>
          )}

          <div style={{ margin: '22px 0', borderTop: '1px solid #e2e8f0' }} />

          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 3fr) minmax(0, 1fr)', gap: '18px' }}>
            <label style={labelStyle}>Payee Type
              {isEmployeePayeeRequest || isSupplierPayeeRequest ? (
                <input value={formData.payeeType} readOnly placeholder={isSupplierPayeeRequest ? 'Select a vendor first' : ''} style={{ ...inputStyle, background: '#f8fafc', cursor: 'not-allowed' }} />
              ) : !formData.requestType ? (
                <input value="" readOnly placeholder="Select request type first" style={{ ...inputStyle, background: '#f8fafc', cursor: 'not-allowed' }} />
              ) : (
                <input name="payeeType" value={formData.payeeType} onChange={updateForm} placeholder="Enter payee type" style={inputStyle} />
              )}
            </label>
            <label style={labelStyle}>Payee Name
              {isEmployeePayeeRequest ? (
                <input value={formData.payeeName} readOnly style={{ ...inputStyle, background: '#f8fafc', cursor: 'not-allowed' }} />
              ) : isSupplierPayeeRequest ? (
                <div style={{ position: 'relative' }}>
                  <input name="payeeName" value={formData.payeeName} onChange={updateForm} onFocus={() => setShowPayeeOptions(true)} onBlur={() => window.setTimeout(() => setShowPayeeOptions(false), 150)} autoComplete="off" placeholder="Search vendors" style={inputStyle} />
                  {showPayeeOptions && (
                    <div style={{ position: 'absolute', zIndex: 10, top: 'calc(100% + 4px)', left: 0, right: 0, maxHeight: '210px', overflowY: 'auto', border: '1px solid #cbd5e1', borderRadius: '7px', background: '#fff', boxShadow: '0 6px 16px rgba(15, 23, 42, 0.14)' }}>
                      {isLoadingPayees ? (
                        <div style={{ padding: '10px 12px', color: '#64748b', fontWeight: '400' }}>Loading vendors…</div>
                      ) : matchingPayeeOptions.length ? matchingPayeeOptions.map((payee) => (
                        <button key={payee.id} type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => selectPayee(payee)} style={{ display: 'block', width: '100%', padding: '10px 12px', border: 0, background: '#fff', color: '#1e293b', cursor: 'pointer', textAlign: 'left', fontSize: '14px' }}>{payee.name}{payee.code ? ` (${payee.code})` : ''}{payee.type ? ` — ${payee.type}` : ''}</button>
                      )) : (
                        <div style={{ padding: '10px 12px', color: '#64748b', fontWeight: '400' }}>No vendors found.</div>
                      )}
                    </div>
                  )}
                </div>
              ) : !formData.requestType ? (
                <input value="" readOnly placeholder="Select request type first" style={{ ...inputStyle, background: '#f8fafc', cursor: 'not-allowed' }} />
              ) : (
                <input name="payeeName" value={formData.payeeName} onChange={updateForm} style={inputStyle} />
              )}
            </label>
            <label style={labelStyle}>ORDER / PO / RO<input name="orderPoRo" value={formData.orderPoRo} onChange={updateForm} style={inputStyle} /></label>
          </div>
          <label style={{ ...labelStyle, marginTop: '18px' }}>Purpose / Business Justification<textarea name="paymentAdvanceRemarks" value={formData.paymentAdvanceRemarks} onChange={updateForm} rows="3" style={{ ...inputStyle, resize: 'vertical' }} /></label>
        </section>

        {['Employee Advance', 'Supplier Advance'].includes(formData.requestType) && (
          <section style={sectionStyle}>
            <h3 style={{ margin: '0 0 18px', color: '#1e293b', fontSize: '16px' }}>Advance Details</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '18px' }}>
              <label style={labelStyle}>Activity / Supply End Date<input type="date" name="activitySupplyEndDate" value={formData.activitySupplyEndDate} onChange={updateForm} style={inputStyle} /></label>
              <label style={labelStyle}>Settlement Due Date<input type="date" name="settlementDueDate" value={formData.settlementDueDate} onChange={updateForm} style={inputStyle} /></label>
            </div>
          </section>
        )}

        {formData.requestType === 'Supplier Invoice' && (
          <section style={sectionStyle}>
            <h3 style={{ margin: '0 0 18px', color: '#1e293b', fontSize: '16px' }}>Supplier Invoice Details</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '18px' }}>
              <label style={labelStyle}>Invoice Number<input name="invoiceNumber" value={formData.invoiceNumber} onChange={updateForm} style={inputStyle} /></label>
              <label style={labelStyle}>Invoice Date<input type="date" name="invoiceDate" value={formData.invoiceDate} onChange={updateForm} style={inputStyle} /></label>
              <label style={labelStyle}>Invoice Gross Amount<input type="number" min="0" step="0.01" name="invoiceGrossAmount" value={formData.invoiceGrossAmount} onChange={updateForm} style={inputStyle} /></label>
              <label style={labelStyle}>Unpaid Invoice Balance<input type="number" min="0" step="0.01" name="unpaidInvoiceBalance" value={formData.unpaidInvoiceBalance} onChange={updateForm} style={inputStyle} /></label>
              <label style={labelStyle}>Invoice Due Date<input type="date" name="invoiceDueDate" value={formData.invoiceDueDate} onChange={updateForm} style={inputStyle} /></label>
              <label style={labelStyle}>GRN / Service Acceptance Reference<input name="grnServiceAcceptanceReference" value={formData.grnServiceAcceptanceReference} onChange={updateForm} style={inputStyle} /></label>
            </div>
          </section>
        )}

        {formData.requestType === 'Salary' && (
          <section style={sectionStyle}>
            <h3 style={{ margin: '0 0 18px', color: '#1e293b', fontSize: '16px' }}>Salary Details</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '18px' }}>
              <label style={labelStyle}>Payroll Period — From<input type="date" name="payrollPeriodStartDate" value={formData.payrollPeriodStartDate} onChange={updateForm} style={inputStyle} /></label>
              <label style={labelStyle}>Payroll Period — To<input type="date" name="payrollPeriodEndDate" value={formData.payrollPeriodEndDate} onChange={updateForm} style={inputStyle} /></label>
              <label style={labelStyle}>Approved Payroll / Batch Reference<input name="approvedPayrollBatchReference" value={formData.approvedPayrollBatchReference} onChange={updateForm} style={inputStyle} /></label>
            </div>
          </section>
        )}

        {formData.requestType === 'Reimbursement' && (
          <section style={sectionStyle}>
            <h3 style={{ margin: '0 0 18px', color: '#1e293b', fontSize: '16px' }}>Reimbursement Details</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '18px' }}>
              <label style={labelStyle}>Expense Date<input type="date" name="expenseDate" value={formData.expenseDate} onChange={updateForm} style={inputStyle} /></label>
              <label style={labelStyle}>Expense / Supporting Reference<input name="expenseSupportingReference" value={formData.expenseSupportingReference} onChange={updateForm} style={inputStyle} /></label>
            </div>
          </section>
        )}

        {formData.requestType === 'Petty Expenses' && (
          <section style={sectionStyle}>
            <h3 style={{ margin: '0 0 18px', color: '#1e293b', fontSize: '16px' }}>Petty Expense Details</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '18px' }}>
              <label style={labelStyle}>Expense Date<input type="date" name="expenseDate" value={formData.expenseDate} onChange={updateForm} style={inputStyle} /></label>
              <label style={labelStyle}>Expense / Replenishment Reference<input name="pettyExpenseReplenishmentReference" value={formData.pettyExpenseReplenishmentReference} onChange={updateForm} style={inputStyle} /></label>
            </div>
          </section>
        )}

        {formData.requestType === 'Statutory Payment' && (
          <section style={sectionStyle}>
            <h3 style={{ margin: '0 0 18px', color: '#1e293b', fontSize: '16px' }}>Statutory Payment Details</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '18px' }}>
              <label style={labelStyle}>Payment Category<input name="statutoryPaymentCategory" value={formData.statutoryPaymentCategory} onChange={updateForm} style={inputStyle} /></label>
              <label style={labelStyle}>Period — From<input type="date" name="statutoryPeriodStartDate" value={formData.statutoryPeriodStartDate} onChange={updateForm} style={inputStyle} /></label>
              <label style={labelStyle}>Period — To<input type="date" name="statutoryPeriodEndDate" value={formData.statutoryPeriodEndDate} onChange={updateForm} style={inputStyle} /></label>
              <label style={labelStyle}>Challan / Assessment Reference<input name="challanAssessmentReference" value={formData.challanAssessmentReference} onChange={updateForm} style={inputStyle} /></label>
            </div>
          </section>
        )}

        <section style={sectionStyle}>
          <h3 style={{ margin: '0 0 18px', color: '#1e293b', fontSize: '16px' }}>Amount Breakup &amp; Justification</h3>
          <div style={{ overflow: 'visible' }}>
            <table style={{ width: '100%', minWidth: '850px', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '15%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '55%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '38px' }} />
              </colgroup>
              <thead><tr style={{ background: '#f8fafc' }}>
                {['Against / Head', 'Document No.', 'Description', 'Amount', ''].map((heading) => <th key={heading || 'actions'} style={{ padding: '10px', textAlign: 'left', fontSize: '12px', color: '#475569', borderBottom: '1px solid #e2e8f0' }}>{heading}</th>)}
              </tr></thead>
              <tbody>
                {lineItems.map((item, index) => <tr key={index}>
                  <td style={{ padding: '10px 6px 10px 0' }}><input value={item.againstHead} onChange={(event) => updateLineItem(index, 'againstHead', event.target.value)} style={inputStyle} /></td>
                  <td style={{ padding: '10px 6px' }}><input value={item.documentNo} onChange={(event) => updateLineItem(index, 'documentNo', event.target.value)} style={inputStyle} /></td>
                  <td style={{ padding: '10px 6px' }}><textarea rows="1" value={item.description} onChange={(event) => updateLineItem(index, 'description', event.target.value)} style={{ ...inputStyle, height: '40px', minHeight: '40px', resize: 'vertical' }} /></td>
                  <td style={{ padding: '10px 6px' }}><input type="number" min="0" step="0.01" value={item.amount} onChange={(event) => updateLineItem(index, 'amount', event.target.value)} style={inputStyle} /></td>
                  <td style={{ padding: '10px 0 10px 6px', width: '38px' }}><button type="button" aria-label="Remove line item" onClick={() => removeLineItem(index)} disabled={lineItems.length === 1} style={{ border: 0, color: lineItems.length === 1 ? '#cbd5e1' : '#dc2626', background: 'transparent', cursor: lineItems.length === 1 ? 'not-allowed' : 'pointer' }}><Trash2 size={18} /></button></td>
                </tr>)}
              </tbody>
            </table>
          </div>
          <button type="button" onClick={() => setLineItems((current) => [...current, createLineItem()])} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginTop: '12px', padding: '8px 12px', border: '1px solid #2563eb', borderRadius: '7px', color: '#2563eb', background: '#fff', cursor: 'pointer', fontWeight: '700' }}><Plus size={16} /> Add Line Item</button>

          <div style={{ maxWidth: '430px', margin: '24px 0 0 auto', borderTop: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', color: '#0f172a', fontSize: '16px' }}><strong>Amount Requested</strong><strong>{amountRequested.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</strong></div>
          </div>
        </section>

        <section style={sectionStyle}>
          <label style={labelStyle}>Outstanding Previous Advance<textarea name="outstandingPreviousAdvance" value={formData.outstandingPreviousAdvance} onChange={updateForm} rows="4" style={{ ...inputStyle, resize: 'vertical' }} /></label>
        </section>

        {showAttachments && (
          <section style={sectionStyle}>
            <h3 style={{ margin: '0 0 6px', color: '#1e293b', fontSize: '16px' }}>{attachmentLabel}</h3>
            <p style={{ margin: '0 0 18px', color: '#64748b', fontSize: '13px' }}>Add the documents that support this payment request.</p>
            <MultiFileUpload
              value={attachmentFiles}
              onFilesChange={setAttachmentFiles}
              maxSizeMB={10}
              helperText="JPG, PNG, or PDF files only. Maximum total size: 10MB."
              disabled={isSaving}
              uploading={isSaving}
            />
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginTop: '18px', color: '#334155', fontSize: '14px', lineHeight: 1.5, cursor: 'pointer' }}>
              <input type="checkbox" name="businessExpenseDeclaration" checked={formData.businessExpenseDeclaration} onChange={updateForm} disabled={isSaving} style={{ width: '16px', height: '16px', marginTop: '3px', accentColor: '#2563eb' }} />
              <span>I confirm this is a business expense and has not been claimed or paid elsewhere. The details and evidence are correct.</span>
            </label>
          </section>
        )}

      </form>
    </div>
  );
};

export default CreatePaymentRequest;
